import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { getFeeSourceLabel } from '../constants/feeSourceLabels';
import { tipTransactionService } from '../services/api';
import './TipTransferDetails.css';

const TipTransferDetails = () => {
  const { paymentIntentId } = useParams();
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    loadTransactionDetails();
    // eslint-disable-next-line
  }, [paymentIntentId]);

  const loadTransactionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await tipTransactionService.getTransaction(paymentIntentId);
      setTransaction(response.data);
    } catch (err) {
      console.error('Failed to load transaction details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  const formatAmount = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const getSoontipFeeTooltip = (transactionData, feeConfig, currency = 'EUR') => {
    if (transactionData?.soontipFeePercentApplied != null || transactionData?.soontipFeeFixedApplied != null) {
      const appliedPercent = transactionData.soontipFeePercentApplied ?? 0;
      const appliedFixedCents = transactionData.soontipFeeFixedApplied ?? 0;
      const appliedFixedAmount = formatAmount(appliedFixedCents / 100, currency);
      const appliedSource = transactionData.soontipFeeSourceApplied
        ? ` (${getFeeSourceLabel(transactionData.soontipFeeSourceApplied)})`
        : '';
      return `Fee storica applicata: ${appliedPercent}% + ${appliedFixedAmount}${appliedSource}`;
    }

    if (!feeConfig) {
      return 'Fee storica non presente.\nConfigurazione fee corrente non disponibile.';
    }

    const percent = feeConfig.soontipFeePercent ?? 0;
    const fixedCents = feeConfig.soontipFeeFixedCents ?? 0;
    const fixedAmount = formatAmount(fixedCents / 100, currency);
    const sourceLabel = getFeeSourceLabel(feeConfig.source);

    return `Fee storica non presente.\nFee corrente per venue: ${percent}% + ${fixedAmount} (${sourceLabel})`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'succeeded': { color: '#28a745', label: 'Successo' },
      'pending': { color: '#ffc107', label: 'In attesa' },
      'failed': { color: '#dc3545', label: 'Fallita' },
      'canceled': { color: '#6c757d', label: 'Cancellata' }
    };

    const config = statusConfig[status] || { color: '#6c757d', label: status };

    return (
      <span style={{
        backgroundColor: config.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="transfer-details-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Caricamento dettagli...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transfer-details-container">
        <div className="error-state">
          <h3>❌ Errore</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/admin/tip-transactions')} className="btn-primary">
            ← Torna alla Lista
          </button>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="transfer-details-container">
        <div className="error-state">
          <h3>⚠️ Transazione non trovata</h3>
          <button onClick={() => navigate('/admin/tip-transactions')} className="btn-primary">
            ← Torna alla Lista
          </button>
        </div>
      </div>
    );
  }

  const transfers = transaction.tipTransfers || [];
  const invoicedCount = transfers.filter(t => t.workerInvoiceId).length;
  const totalTransfers = transfers.length;

  // Paginazione
  const totalPages = Math.ceil(totalTransfers / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTransfers = transfers.slice(startIndex, endIndex);

  const getWorkerName = (transfer) => {
    if (!transfer.worker) return '-';
    return `${transfer.worker.firstName || ''} ${transfer.worker.lastName || ''}`.trim() || '-';
  };

  const getWorkerEmail = (transfer) => {
    if (!transfer.worker) return '-';
    return transfer.worker.email || '-';
  };

  const isTeamPercentageMode = transaction.distributionSummary === 'TEAM_PERCENTAGE';

  const teamGroups = isTeamPercentageMode ? (() => {
    const groups = new Map();

    // Build group metadata from snapshots (historical: name + pcts at payment time)
    transfers.forEach(t => {
      const snap = t.metadata?.teamSnapshot;
      if (!snap) return;
      snap.forEach(s => {
        if (!groups.has(s.teamId)) {
          groups.set(s.teamId, {
            teamId: s.teamId,
            name: s.nameSnapshot,
            configuredPct: s.teamConfiguredPct ?? null,
            normalizedPct: s.teamPct ?? null,
            transfers: [],
          });
        }
      });
    });

    groups.set('__multi__', { teamId: '__multi__', name: 'Multi-team', configuredPct: null, normalizedPct: null, transfers: [] });

    // Empty teams from historical snapshot (normalizedPct==null means team was empty at payment time)
    (transaction.activeRuleTeamAllocations || []).forEach(a => {
      if (!groups.has(a.teamId)) {
        groups.set(a.teamId, {
          teamId: a.teamId,
          name: a.nameSnapshot || a.teamId,
          configuredPct: a.configuredPct ?? a.percentage ?? null,
          normalizedPct: a.normalizedPct ?? null,
          transfers: [],
        });
      }
    });

    transfers.forEach(t => {
      const snap = t.metadata?.teamSnapshot;
      if (snap && snap.length > 1) {
        groups.get('__multi__').transfers.push(t);
      } else {
        const tid = snap?.[0]?.teamId || t.worker?.team?.id;
        if (tid && groups.has(tid)) groups.get(tid).transfers.push(t);
        else {
          if (!groups.has('__other__')) groups.set('__other__', { teamId: '__other__', name: 'Altro', configuredPct: null, normalizedPct: null, transfers: [] });
          groups.get('__other__').transfers.push(t);
        }
      }
    });
    // Mark groups whose workers all ended up in __multi__ (so we can show a clearer label)
    const multiXfers = groups.get('__multi__')?.transfers || [];
    groups.forEach((grp, tid) => {
      if (tid === '__multi__') return;
      grp.hasMultiTeamWorkers = multiXfers.some(t =>
        (t.metadata?.teamSnapshot || []).some(s => s.teamId === tid)
      );
    });
    return [...groups.values()].filter(g => g.transfers.length > 0 || g.configuredPct != null);
  })() : [];

  const recipientModeLabel = transaction.recipientModeSummary === 'NAMED_SINGLE' ? 'Nominale' : 'Staff';
  const distributionModeLabel =
    transaction.distributionSummary === 'WORKER_PERCENTAGE'
      ? 'Percettore %'
      : transaction.distributionSummary === 'TEAM_PERCENTAGE'
        ? 'Team %'
        : 'Standard';

  const customRuleDetails = (transaction.tipTransfers || [])
    .filter((transfer) => transfer?.distributionModeApplied === 'WORKER_PERCENTAGE' || transfer?.distributionModeApplied === 'TEAM_PERCENTAGE')
    .map((transfer) => ({
      worker: transfer.worker ? `${transfer.worker.firstName || ''} ${transfer.worker.lastName || ''}`.trim() || transfer.worker.email : '-',
      mode: transfer.distributionModeApplied,
      percentage: Number(transfer.appliedPercentage) || null,
    }));

  return (
    <div className="transfer-details-container">
      {/* Header */}
      <div className="page-header">
        <button onClick={() => navigate('/admin/tip-transactions')} className="btn-back">
          ← Torna alla Lista
        </button>
        <h1>🔀 Dettaglio Transfers</h1>
        <p className="subtitle">Payment Intent: {transaction.id}</p>
      </div>

      {/* Transaction Summary */}
      <div className="transaction-summary">
        <div className="summary-card">
          <div className="summary-label">Data Transazione</div>
          <div className="summary-value">{formatDate(transaction.transactionDate)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Importo Totale</div>
          <div className="summary-value">{formatAmount(transaction.amount, transaction.currency)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Status</div>
          <div className="summary-value">{getStatusBadge(transaction.status)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Venue</div>
          <div className="summary-value">
            {transaction.venue?.name || '-'}
            {transaction.venue?.tenant && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {transaction.venue.tenant.company_name}
              </div>
            )}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Device</div>
          <div className="summary-value">{transaction.device?.label || '-'}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Transfers</div>
          <div className="summary-value">{totalTransfers}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Destinatario</div>
          <div className="summary-value">{recipientModeLabel}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Distribuzione</div>
          <div className="summary-value">{distributionModeLabel}</div>
        </div>
        {customRuleDetails.length > 0 && (
          <div className="summary-card">
            <div className="summary-label">Dettaglio %</div>
            <div className="summary-value" style={{ fontSize: '12px', lineHeight: 1.5 }}>
              {customRuleDetails.map((item, index) => (
                <div key={`${item.worker}-${index}`}>
                  {item.worker}: {item.percentage != null ? `${item.percentage.toFixed(2)}%` : 'n.d.'}
                </div>
              ))}
            </div>
          </div>
        )}
        {transaction.activeRuleTeamAllocations && transaction.activeRuleTeamAllocations.length > 0 && (
          <div className="summary-card">
            <div className="summary-label">Team configurati <span style={{ fontWeight: 400, fontSize: '10px', color: '#94a3b8' }}>(snapshot)</span></div>
            <div className="summary-value" style={{ fontSize: '12px', lineHeight: 1.5 }}>
              {transaction.activeRuleTeamAllocations.map((a) => (
                <div key={a.teamId}>
                  {a.nameSnapshot || a.teamId}:{' '}
                  <strong>
                    {(a.configuredPct ?? a.percentage) != null
                      ? `${a.configuredPct ?? a.percentage}%`
                      : 'n.d.'}
                  </strong>
                  {a.normalizedPct != null && Math.abs(a.normalizedPct - (a.configuredPct ?? a.percentage)) > 0.01 && (
                    <span style={{ color: '#6366f1', marginLeft: 4 }}>→ {a.normalizedPct.toFixed(2)}%</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="summary-card">
          <div className="summary-label">Fatturati</div>
          <div className="summary-value">
            {invoicedCount}/{totalTransfers}
            {invoicedCount === totalTransfers && totalTransfers > 0 && ' ✅'}
          </div>
        </div>
        {transaction.selectedWorkerSummary && (
          <div className="summary-card">
            <div className="summary-label">Worker nominale</div>
            <div className="summary-value" style={{ fontSize: '12px' }}>{transaction.selectedWorkerSummary}</div>
          </div>
        )}
      </div>

      {/* Transfers Table */}
      <div className="transfers-section">
        <div className="section-header">
          <h2>📤 Transfers ({transfers.length})</h2>
          <div className="pagination-info">
            Pagina {currentPage} di {totalPages} • Mostrando {startIndex + 1}-{Math.min(endIndex, totalTransfers)} di {totalTransfers}
          </div>
        </div>

        {transfers.length === 0 ? (
          <div className="empty-state">
            <p>Nessun transfer trovato per questa transazione</p>
          </div>
        ) : isTeamPercentageMode ? (
          /* ── TEAM_PERCENTAGE: grouped by team ── */
          <div className="table-wrapper">
            <table className="transfers-table">
              <thead>
                <tr>
                  <th className="col-worker">Worker</th>
                  <th className="col-email">Email</th>
                  <th className="col-amount">Importo<br />Netto</th>
                  <th className="col-fee">Fee<br />Totale</th>
                  <th className="col-amount">%<br />Applicata</th>
                  <th className="col-status">Reversal</th>
                  <th className="col-amount">Importo<br />Reversato</th>
                  <th className="col-invoice">Stato<br />Fattura</th>
                  <th className="col-invoice-num">Numero<br />Fattura</th>
                </tr>
              </thead>
              <tbody>
                {teamGroups.map((group) => {
                  const isEmpty = group.transfers.length === 0;
                  const groupNet = group.transfers.reduce((s, t) => s + (t.amountNet || t.amount || 0), 0);
                  // normalizedPct is the historically stored effective %; only show arrow if differs from configured
                  const showNormalized = !isEmpty && group.normalizedPct != null && group.configuredPct != null
                    && Math.abs(group.normalizedPct - group.configuredPct) > 0.01;
                  return (
                    <>
                      <tr key={`team-${group.teamId}`} className={`team-group-header${isEmpty ? ' team-group-empty' : ''}`}>
                        <td colSpan={9}>
                          <span className="team-group-name">{group.name}</span>
                          {group.configuredPct != null && (
                            <>
                              {showNormalized ? (
                                <>
                                  <span className="team-group-pct team-group-pct-orig">{group.configuredPct}%</span>
                                  <span className="team-group-pct-arrow">→</span>
                                  <span className="team-group-pct">{group.normalizedPct.toFixed(2)}%</span>
                                </>
                              ) : (
                                <span className="team-group-pct">{group.configuredPct}%</span>
                              )}
                            </>
                          )}
                          {isEmpty
                            ? (group.hasMultiTeamWorkers
                                ? <span className="team-group-multimoved-label">↓ worker nel gruppo multi-team</span>
                                : <span className="team-group-empty-label">Nessun worker attivo</span>)
                            : <span className="team-group-total">{formatAmount(groupNet / 100, transaction.currency)}</span>
                          }
                        </td>
                      </tr>
                      {group.transfers.map((transfer) => {
                        const isReversed = transfer.hasReversal;
                        const isFullReversal = transfer.reversed === true;
                        const isPartialReversal = isReversed && !isFullReversal;
                        const workerName = getWorkerName(transfer);
                        const workerEmail = getWorkerEmail(transfer);
                        const hasFeeValues = transfer.feeTotal != null || transfer.feeSoontip != null || transfer.feeStripe != null;
                        const feeTotal = (transfer.feeTotal || 0) / 100;
                        const feeSoontip = (transfer.feeSoontip || 0) / 100;
                        const feeStripe = (transfer.feeStripe || 0) / 100;
                        const parsedAppliedPct = Number(transfer.appliedPercentage);
                        const appliedPctLabel = Number.isFinite(parsedAppliedPct) && parsedAppliedPct > 0 ? `${parsedAppliedPct.toFixed(2)}%` : '-';
                        const isMultiTeam = transfer.metadata?.multiTeam === true;
                        const teamSnap = transfer.metadata?.teamSnapshot;
                        const teamSnapLabel = isMultiTeam && Array.isArray(teamSnap)
                          ? teamSnap.map(t => t.nameSnapshot).join(' + ')
                          : null;

                        return (
                          <tr key={transfer.id} className="worker-row-indented" title={`Transfer ID: ${transfer.id}`}>
                            <td className="col-worker">
                              <strong>{workerName}</strong>
                              {isMultiTeam && (
                                <span className="multi-team-badge" title={teamSnapLabel || 'Riceve quote da più team configurati'}>
                                  ⚠️ {teamSnapLabel || 'Multi-team'}
                                </span>
                              )}
                              {isMultiTeam && Array.isArray(teamSnap) && teamSnap.length > 0 && (
                                <div className="multi-team-breakdown">
                                  {teamSnap.map(s => (
                                    <span key={s.teamId} className="multi-team-contrib-item">
                                      {s.nameSnapshot}: <strong>{s.pctSnapshot != null ? `${s.pctSnapshot.toFixed(2)}%` : '-'}</strong>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="col-email"><span className="email-text">{workerEmail}</span></td>
                            <td className="col-amount">
                              <strong>{formatAmount((transfer.amountNet || transfer.amount) / 100, transaction.currency)}</strong>
                            </td>
                            <td className="col-fee">
                              {hasFeeValues ? (
                                <div className="fee-cell-wrapper">
                                  <div className="fee-total">{formatAmount(feeTotal, transaction.currency)}</div>
                                  <div className="fee-breakdown">
                                    <span title={getSoontipFeeTooltip(transaction, transaction.feeConfig, transaction.currency)}>SoonTip: {formatAmount(feeSoontip, transaction.currency)}</span>
                                    <span>Stripe: {formatAmount(feeStripe, transaction.currency)}</span>
                                  </div>
                                </div>
                              ) : '-'}
                            </td>
                            <td className="col-amount">{appliedPctLabel}</td>
                            <td className="col-status">
                              {isFullReversal ? <span className="badge badge-danger">🔴 Totale</span>
                                : isPartialReversal ? <span className="badge badge-warning">🟡 Parziale</span>
                                : <span style={{ color: '#999' }}>-</span>}
                            </td>
                            <td className="col-amount">
                              {transfer.amountReversed > 0
                                ? <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{formatAmount(transfer.amountReversed / 100, transaction.currency)}</span>
                                : <span style={{ color: '#999' }}>-</span>}
                            </td>
                            <td className="col-invoice">
                              {transfer.workerInvoice
                                ? <span className="badge badge-success">✅ Fatturato</span>
                                : <span className="badge badge-secondary">⏳ No</span>}
                            </td>
                            <td className="col-invoice-num">
                              {transfer.workerInvoice ? (
                                <a href={`/admin/invoices?invoiceNumber=${encodeURIComponent(transfer.workerInvoice.invoiceNumber)}`} className="invoice-number-link" title={`Fattura ${transfer.workerInvoice.invoiceNumber}`}>
                                  {transfer.workerInvoice.invoiceNumber}
                                </a>
                              ) : <span style={{ color: '#999' }}>-</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* ── Standard paginated table ── */
          <>
            <div className="table-wrapper">
              <table className="transfers-table">
                <thead>
                  <tr>
                    <th className="col-worker">Worker</th>
                    <th className="col-email">Email</th>
                    <th className="col-amount">Importo<br />Netto</th>
                    <th className="col-fee">Fee<br />Totale</th>
                    <th className="col-amount">%<br />Applicata</th>
                    <th className="col-status">Reversal</th>
                    <th className="col-amount">Importo<br />Reversato</th>
                    <th className="col-invoice">Stato<br />Fattura</th>
                    <th className="col-invoice-num">Numero<br />Fattura</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransfers.map((transfer) => {
                    const isReversed = transfer.hasReversal;
                    const isFullReversal = transfer.reversed === true;
                    const isPartialReversal = isReversed && !isFullReversal;
                    const workerName = getWorkerName(transfer);
                    const workerEmail = getWorkerEmail(transfer);
                    const hasFeeValues = transfer.feeTotal != null || transfer.feeSoontip != null || transfer.feeStripe != null;
                    const feeTotal = (transfer.feeTotal || 0) / 100;
                    const feeSoontip = (transfer.feeSoontip || 0) / 100;
                    const feeStripe = (transfer.feeStripe || 0) / 100;
                    const parsedAppliedPct = Number(transfer.appliedPercentage);
                    const hasAppliedWeightedPct = Number.isFinite(parsedAppliedPct) && parsedAppliedPct > 0;
                    const appliedPctLabel = hasAppliedWeightedPct ? `${parsedAppliedPct.toFixed(2)}%` : '-';

                    return (
                      <tr key={transfer.id} title={`Transfer ID: ${transfer.id}`}>
                        <td className="col-worker">
                          <strong>{workerName}</strong>
                          {transfer.worker?.team && (
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '1px' }}>
                              {transfer.worker.team.name}
                            </div>
                          )}
                        </td>
                        <td className="col-email">
                          <span className="email-text">{workerEmail}</span>
                        </td>
                        <td className="col-amount">
                          <strong>{formatAmount((transfer.amountNet || transfer.amount) / 100, transaction.currency)}</strong>
                        </td>
                        <td className="col-fee">
                          {hasFeeValues ? (
                            <div className="fee-cell-wrapper">
                              <div className="fee-total">{formatAmount(feeTotal, transaction.currency)}</div>
                              <div className="fee-breakdown">
                                <span title={getSoontipFeeTooltip(transaction, transaction.feeConfig, transaction.currency)}>SoonTip: {formatAmount(feeSoontip, transaction.currency)}</span>
                                <span>Stripe: {formatAmount(feeStripe, transaction.currency)}</span>
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="col-amount" title={transfer.distributionModeApplied || 'STANDARD'}>
                          {appliedPctLabel}
                        </td>
                        <td className="col-status">
                          {isFullReversal ? (
                            <span className="badge badge-danger">🔴 Totale</span>
                          ) : isPartialReversal ? (
                            <span className="badge badge-warning">🟡 Parziale</span>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                        <td className="col-amount">
                          {transfer.amountReversed > 0 ? (
                            <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                              {formatAmount(transfer.amountReversed / 100, transaction.currency)}
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                        <td className="col-invoice">
                          {transfer.workerInvoice ? (
                            <span className="badge badge-success">✅ Fatturato</span>
                          ) : (
                            <span className="badge badge-secondary">⏳ No</span>
                          )}
                        </td>
                        <td className="col-invoice-num">
                          {transfer.workerInvoice ? (
                            <a
                              href={`/admin/invoices?invoiceNumber=${encodeURIComponent(transfer.workerInvoice.invoiceNumber)}`}
                              className="invoice-number-link"
                              title={`Vai alla fattura ${transfer.workerInvoice.invoiceNumber} (generata il ${formatDate(transfer.workerInvoice.generatedAt)})`}
                            >
                              {transfer.workerInvoice.invoiceNumber}
                            </a>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Additional Info */}
      {transaction.stripePaymentIntentId && (
        <>
          <div className="info-section">
            <h3>ℹ️ Informazioni Aggiuntive</h3>
            <div className="info-grid">
              <div className="info-item">
                <strong>Stripe Payment Intent ID:</strong>
                <span className="mono-text">{transaction.stripePaymentIntentId}</span>
              </div>
              {transaction.stripeChargeId && (
                <div className="info-item">
                  <strong>Stripe Charge ID:</strong>
                  <span className="mono-text">{transaction.stripeChargeId}</span>
                </div>
              )}
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default TipTransferDetails;
